import { useTranslation } from '../../../i18n';
import { SectionHead } from '../SectionHead';
import { TaskRow } from './TaskRow';
import type { PlanningTask } from '../../../types/dashboard';
import type { TaskChecklistGroup } from '../../../store/useMyChecklistStore';
import { formatHours } from '../../../utils/dashboardData';

interface UpNextCardProps {
  doneTasks: PlanningTask[];
  currentTask: PlanningTask | null;
  upcomingTasks: PlanningTask[];
  remainingMinutes: number;
  /** Today's checklists keyed by task id, so each row can show its own. */
  checklistsByTaskId?: Record<string, TaskChecklistGroup>;
  checklistDate?: string;
}

export function UpNextCard({
  doneTasks,
  currentTask,
  upcomingTasks,
  remainingMinutes,
  checklistsByTaskId,
  checklistDate,
}: UpNextCardProps) {
  const { t } = useTranslation('dashboard');

  const isEmpty = !currentTask && upcomingTasks.length === 0 && doneTasks.length === 0;

  const checklistFor = (task: PlanningTask) =>
    task.taskId ? checklistsByTaskId?.[task.taskId] : undefined;

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      <SectionHead
        title={t('upNext.title')}
        sub={
          upcomingTasks.length > 0
            ? t('upNext.count', { count: upcomingTasks.length })
            : undefined
        }
        action={
          remainingMinutes > 0 && (
            <span className="font-mono-num text-xs text-ink-muted dark:text-slate-400">
              {t('upNext.remaining', { hours: (remainingMinutes / 60).toFixed(1).replace('.', ',') })}
            </span>
          )
        }
      />

      <div className="mt-4 space-y-2">
        {isEmpty ? (
          <div className="rounded-lg border border-dashed border-border bg-surface-muted dark:bg-slate-800/50 dark:border-slate-700 p-5 text-center text-sm text-ink-muted dark:text-slate-400">
            {t('upNext.empty')}
          </div>
        ) : (
          <>
            {doneTasks.slice(-2).map((task) => (
              <TaskRow
                key={task.planningEntryTaskId}
                task={task}
                state="done"
                checklist={checklistFor(task)}
                checklistDate={checklistDate}
              />
            ))}
            {currentTask && (
              <TaskRow
                key={currentTask.planningEntryTaskId}
                task={currentTask}
                state="now"
                checklist={checklistFor(currentTask)}
                checklistDate={checklistDate}
              />
            )}
            {upcomingTasks.map((task) => (
              <TaskRow
                key={task.planningEntryTaskId}
                task={task}
                state="next"
                checklist={checklistFor(task)}
                checklistDate={checklistDate}
              />
            ))}
          </>
        )}
        {remainingMinutes > 0 && upcomingTasks.length > 0 && (
          <div className="pt-2 mt-1 border-t border-border-subtle dark:border-slate-800 text-[11px] text-ink-faint dark:text-slate-500 font-mono-num text-right">
            ∑ {formatHours(remainingMinutes)}
          </div>
        )}
      </div>
    </div>
  );
}
